import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Upload, Trash2, Type, Copy, Check, FileText, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const FontsTab = () => {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);
  
  // Upload form
  const [newFont, setNewFont] = useState({
    name: "",
    family: "",
    file: null
  });

  const fetchFonts = async () => {
    try {
      const res = await axios.get(`${API}/fonts`);
      setFonts(res.data);
    } catch (err) {
      console.error("Error fetching fonts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFonts();
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Auto-fill name and family from filename
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      setNewFont({
        ...newFont,
        file,
        name: newFont.name || baseName,
        family: newFont.family || baseName.replace(/[-_]/g, " ")
      });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newFont.file || !newFont.name || !newFont.family) {
      setError("Please fill in all fields and select a font file");
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", newFont.file);
      formData.append("name", newFont.name);
      formData.append("family", newFont.family);

      await axios.post(`${API}/fonts/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      setNewFont({ name: "", family: "", file: null });
      // Reset file input
      const fileInput = document.getElementById("font-file-input");
      if (fileInput) fileInput.value = "";
      
      fetchFonts();
    } catch (err) {
      setError(err.response?.data?.detail || "Error uploading font");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (fontId) => {
    if (!window.confirm("Are you sure you want to delete this font?")) return;

    try {
      await axios.delete(`${API}/fonts/${fontId}`, { headers: getAuthHeader() });
      fetchFonts();
    } catch (err) {
      alert("Error deleting font");
    }
  };

  const copyFontFamily = (family) => {
    navigator.clipboard.writeText(family);
    setCopiedId(family);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-white/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-2">
          Custom Fonts
        </h2>
        <p className="text-zinc-500 text-sm">
          Upload custom fonts to use in homepage sections and text elements
        </p>
      </div>

      {/* Upload Form */}
      <div className="bg-zinc-900/50 border border-white/5 p-6">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload New Font
        </h3>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-500 text-sm mb-4">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 uppercase mb-2">Font Name</label>
              <input
                type="text"
                value={newFont.name}
                onChange={(e) => setNewFont({ ...newFont, name: e.target.value })}
                placeholder="e.g., Abolition Bold"
                className="w-full px-4 py-2 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 uppercase mb-2">
                CSS Font Family
                <span className="text-zinc-600 ml-1">(for styling)</span>
              </label>
              <input
                type="text"
                value={newFont.family}
                onChange={(e) => setNewFont({ ...newFont, family: e.target.value })}
                placeholder="e.g., Abolition"
                className="w-full px-4 py-2 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-500 uppercase mb-2">Font File</label>
            <div className="border-2 border-dashed border-white/20 hover:border-orange-500/50 transition-colors p-6 text-center">
              <input
                id="font-file-input"
                type="file"
                accept=".woff,.woff2,.ttf,.otf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <label htmlFor="font-file-input" className="cursor-pointer block">
                <FileText className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                {newFont.file ? (
                  <p className="text-orange-500 font-medium">{newFont.file.name}</p>
                ) : (
                  <>
                    <p className="text-white mb-1">Click to select font file</p>
                    <p className="text-zinc-500 text-sm">Supported: WOFF, WOFF2, TTF, OTF (max 5MB)</p>
                  </>
                )}
              </label>
            </div>
          </div>

          {uploading && uploadProgress > 0 && (
            <div>
              <div className="flex justify-between text-xs text-zinc-400 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={uploading || !newFont.file}
            className="bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Upload Font"}
          </Button>
        </form>
      </div>

      {/* Fonts List */}
      <div>
        <h3 className="text-white font-bold mb-4">
          Uploaded Fonts ({fonts.length})
        </h3>

        {fonts.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/30 border border-white/5">
            <Type className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No custom fonts uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fonts.map((font) => (
              <motion.div
                key={font.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-zinc-900/50 border border-white/5 p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-white font-bold">{font.name}</h4>
                    <p className="text-zinc-500 text-xs">{font.filename}</p>
                  </div>
                  <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-[10px] uppercase">
                    {font.format}
                  </span>
                </div>

                {/* Font Preview */}
                <div
                  className="p-4 bg-black/50 border border-white/10 mb-3 text-center"
                  style={{ fontFamily: font.family }}
                >
                  <span className="text-white text-2xl">Aa Bb Cc 123</span>
                </div>

                {/* Font Family Copy */}
                <div className="flex items-center gap-2 mb-3">
                  <code className="flex-1 px-2 py-1 bg-black text-zinc-400 text-xs font-mono truncate">
                    font-family: &apos;{font.family}&apos;
                  </code>
                  <button
                    onClick={() => copyFontFamily(font.family)}
                    className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                    title="Copy font-family"
                  >
                    {copiedId === font.family ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <button
                    onClick={() => handleDelete(font.id)}
                    className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                    title="Delete font"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Instructions */}
      <div className="bg-zinc-900/30 border border-white/5 p-6">
        <h3 className="text-white font-bold mb-3">How to Use Custom Fonts</h3>
        <ul className="text-zinc-400 text-sm space-y-2">
          <li>• Uploaded fonts are automatically available site-wide</li>
          <li>• Use the CSS font-family name in the Homepage section editor</li>
          <li>• Fonts are loaded on-demand for optimal performance</li>
          <li>• Recommended formats: WOFF2 (best compression), WOFF (wide support)</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default FontsTab;
